import {
  getBracketDetail as getBracketDetailApi,
  getEventDetail as getEventDetailApi,
  getEvents as getEventsApi,
  getMyEvents as getMyEventsApi,
} from '@/services'
import {
  Bracket,
  Event,
  GetBracketDetailResponse,
  GetEventDetailResponse,
  GetEventsResponse,
  PayloadGetEvents,
  PayloadGetMyEvents,
  ResultGetEvents,
} from '@/types'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

interface EventStore {
  isLoading: boolean
  isLoadingMyEvents: boolean
  isLoadingEvents: boolean
  resMyEvents: ResultGetEvents
  resEvents: ResultGetEvents
  event: Event | null
  bracket: Bracket | null
  isLoadingBracketData: boolean
}

export const GetMyEventsPrefix = '@Event/GetMyEvents'
export const GetEventsPrefix = '@Event/GetEvents'
export const GetEventDetailPrefix = '@Event/GetEventDetail'
export const GetBracketDetailPrefix = '@Event/GetBracketDetail'
export const GetBracketPlayersPrefix = '@Event/GetBracketPlayers'
export const GetBracketTeamsPrefix = '@Event/GetBracketTeams'

export const getMyEvents = createAsyncThunk(GetMyEventsPrefix, async (payload: PayloadGetMyEvents) => {
  return await getMyEventsApi(payload)
})

export const getEvents = createAsyncThunk(GetEventsPrefix, async (payload: PayloadGetEvents) => {
  return await getEventsApi(payload)
})

export const getEventDetail = createAsyncThunk(GetEventDetailPrefix, async (eventId: string) => {
  return await getEventDetailApi(eventId)
})

export const getBracketDetail = createAsyncThunk(GetBracketDetailPrefix, async (bracketId: string) => {
  return await getBracketDetailApi(bracketId)
})

const initialState: EventStore = {
  isLoading: false,
  isLoadingMyEvents: false,
  isLoadingEvents: false,
  resMyEvents: {
    offset: 0,
    limit: 10,
    total: 0,
    hits: [],
    totalValueRelation: null,
    hasMore: false,
    hasPrevious: false,
    empty: false,
  },
  resEvents: {
    offset: 0,
    limit: 10,
    total: 0,
    hits: [],
    totalValueRelation: null,
    hasMore: false,
    hasPrevious: false,
    empty: false,
  },
  event: null,
  bracket: null,
  isLoadingBracketData: false,
}

const eventSlice = createSlice({
  name: 'EVENT_SLICE',
  initialState,
  reducers: {
    resetEvent: (state) => {
      state.resMyEvents = {
        offset: 0,
        limit: 10,
        total: 0,
        hits: [],
        totalValueRelation: null,
        hasMore: false,
        hasPrevious: false,
        empty: false,
      }
      state.bracket = null
      state.event = null
      state.isLoading = false
      state.isLoadingMyEvents = false
      state.isLoadingEvents = false
      state.isLoadingBracketData = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMyEvents.pending, (state) => {
      state.isLoadingMyEvents = true
    })
    builder.addCase(getMyEvents.fulfilled, (state, action: PayloadAction<GetEventsResponse>) => {
      if (action.payload.result.offset === 0) {
        state.resMyEvents = action.payload.result
      }
      if (action.payload.result.offset !== 0) {
        const updatedResMyEvents = {
          ...state.resMyEvents,
          empty: action.payload.result.empty,
          hasMore: action.payload.result.hasMore,
          hasPrevious: action.payload.result.hasPrevious,
          hits: [
            ...new Map(
              [...state.resMyEvents.hits, ...action.payload.result.hits].map((item) => [item.leagueId, item])
            ).values(),
          ],
          limit: action.payload.result.limit,
          offset: action.payload.result.offset,
          total: action.payload.result.total,
          totalValueRelation: action.payload.result.totalValueRelation,
        }
        state.resMyEvents = updatedResMyEvents
      }

      state.isLoadingMyEvents = false
    })
    builder.addCase(getMyEvents.rejected, (state) => {
      state.isLoadingMyEvents = false
    })

    builder.addCase(getEvents.pending, (state) => {
      state.isLoadingEvents = true
    })
    builder.addCase(getEvents.fulfilled, (state, action: PayloadAction<GetEventsResponse>) => {
      if (action.payload.result.offset === 0) {
        state.resEvents = action.payload.result
      }
      if (action.payload.result.offset !== 0) {
        const updatedResEvents = {
          ...state.resEvents,
          empty: action.payload.result.empty,
          hasMore: action.payload.result.hasMore,
          hasPrevious: action.payload.result.hasPrevious,
          hits: [
            ...new Map(
              [...state.resEvents.hits, ...action.payload.result.hits].map((item) => [item.leagueId, item])
            ).values(),
          ],
          limit: action.payload.result.limit,
          offset: action.payload.result.offset,
          total: action.payload.result.total,
          totalValueRelation: action.payload.result.totalValueRelation,
        }
        state.resEvents = updatedResEvents
      }

      state.isLoadingEvents = false
    })
    builder.addCase(getEvents.rejected, (state) => {
      state.isLoadingEvents = false
    })

    builder.addCase(getEventDetail.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getEventDetail.fulfilled, (state, action: PayloadAction<GetEventDetailResponse>) => {
      state.event = action.payload.result
      state.isLoading = false
    })
    builder.addCase(getEventDetail.rejected, (state) => {
      state.isLoading = false
    })

    builder.addCase(getBracketDetail.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getBracketDetail.fulfilled, (state, action: PayloadAction<GetBracketDetailResponse>) => {
      state.bracket = action.payload.result
      state.isLoading = false
    })
    builder.addCase(getBracketDetail.rejected, (state) => {
      state.isLoading = false
    })
  },
})

const { reducer, actions } = eventSlice

export const { resetEvent } = actions

export default reducer
